import {
  DialogContent,
  Dialog,
  DialogProps,
  DialogActions,
  useTheme,
  useMediaQuery,
  Grid,
  useEventCallback,
} from '@mui/material';
import { styled } from '@mui/styles';
import { PmiButton } from 'shell/components/shared/button';
import { memo } from 'react';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { CastedParamsVM, ProprietarioVM } from './form';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {}
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

interface ProprietarioModalVM {
  fiscalCode?: string;
  surname?: string;
  name?: string;
  dateBorn?: DateTime;
  municipality?: string;
  address?: string;
}

interface ModalAddProprietarioProps {
  open: boolean;
  castedParams?: CastedParamsVM;
  onCloseConfirm: (isEdit?: boolean) => void;
  onClose: () => void;
  proprietario?: ProprietarioVM;
}

const ModalAddProprietarioComponent = ({
  open,
  castedParams,
  onClose,
  onCloseConfirm,
  proprietario,
}: ModalAddProprietarioProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

  const insertProprietario = useHttpClient(`${__PRACTICE_API_URL__}/proprietario/inserimento`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const editProprietario = useHttpClient(`${__PRACTICE_API_URL__}/proprietario/aggiornamento`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onSubmit = useEventCallback((values: ProprietarioModalVM) => {
    const data = {
      key: proprietario ? proprietario.proprietarioBE?.key : undefined,
      nome: values.name || undefined,
      dataNascita: values.dateBorn ? values.dateBorn.toFormat('yyyy-MM-dd') : undefined,
      cognome: values.surname || undefined,
      indirizzo: values.address || undefined,
      codFiscale: values.fiscalCode || undefined,
      comune: values.municipality || undefined,
      progressivoPratica: castedParams?.progressivoPratica || undefined,
      tipoRegistro: castedParams?.tipoRegistro || undefined,
      codiceEnte: castedParams?.codiceEnte || undefined,
      idAnnoPratica: castedParams?.idAnnoPratica || undefined,
    };

    if (Boolean(proprietario)) {
      // modifica
      (
        editProprietario({
          data,
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        onCloseConfirm(true);
      });
      return;
    }

    // inserimento
    (
      insertProprietario({
        data,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      onCloseConfirm();
    });
  });

  const formContext = useFormik<ProprietarioModalVM>({
    initialValues: {
      address: proprietario?.proprietarioBE?.indirizzo || '',
      fiscalCode: proprietario?.proprietarioBE?.codFiscale || '',
      dateBorn: proprietario?.proprietarioBE?.dataNascita
        ? DateTime.fromISO(proprietario?.proprietarioBE?.dataNascita)
        : undefined,
      municipality: proprietario?.proprietarioBE?.comune || '',
      name: proprietario?.proprietarioBE?.nome || '',
      surname: proprietario?.proprietarioBE?.cognome || '',
    },
    validationSchema: yup.object().shape({
      dateBorn: yup.date().nullable().typeError('Data non valida'),
    }),
    onSubmit,
  });

  return (
    <PmiDialog
      open={open}
      fullScreen={fullScreen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Codice fiscale"
              aria-label="Codice fiscale"
              id="fiscalCode"
              fullWidth
              onChange={(event) => formContext.setFieldValue('fiscalCode', event.target.value)}
              value={formContext.values.fiscalCode || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Cognome"
              aria-label="Cognome"
              id="surname"
              fullWidth
              onChange={(event) => formContext.setFieldValue('surname', event.target.value)}
              value={formContext.values.surname || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Nome"
              aria-label="Nome"
              id="name"
              fullWidth
              onChange={(event) => formContext.setFieldValue('name', event.target.value)}
              value={formContext.values.name || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Data di nascita"
              aria-label="Data di nascita"
              value={formContext.values?.dateBorn || null}
              onChange={(date) => {
                formContext.setFieldValue('dateBorn', date);
              }}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateBorn"
                  fullWidth
                  helperText={formContext.errors?.dateBorn}
                  error={formContext.touched?.dateBorn && Boolean(formContext.errors?.dateBorn)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Comune di nascita"
              aria-label="Comune di nascita"
              id="municipality"
              fullWidth
              onChange={(event) => formContext.setFieldValue('municipality', event.target.value)}
              value={formContext.values.municipality || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Indirizzo"
              aria-label="Indirizzo"
              id="address"
              fullWidth
              onChange={(event) => formContext.setFieldValue('address', event.target.value)}
              value={formContext.values.address || ''}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton variant="contained" color="primary" onClick={() => formContext.handleSubmit()}>
          Conferma
        </PmiButton>
        <PmiButton
          variant="outlined"
          color="cdmBrand"
          onClick={() => {
            onClose();
          }}
        >
          Annulla
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalAddProprietarioComponent.displayName = 'Modal Add Proprietario component';

const ModalAddProprietario = memo(ModalAddProprietarioComponent);

export default ModalAddProprietario;
